/**
 * =========================================================================================
 * Cashfree Payment Callback Page
 * =========================================================================================
 * Handles redirect from Cashfree after any payment:
 *   - Proration upgrade  → ?order_id=prorate_sub_XXX_TIMESTAMP
 *   - New subscription   → ?order_id=sub_XXX or ?subscriptionId=sub_XXX
 * Verifies payment via redirectStatus and syncs with Cashfree.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import * as Icon from 'ubs-icons';
import UButton from 'ubs-framework/v2/UButton';
import { sendData } from '../../../../../store/actions';
import useSocket from '../../../../common/useSocket';
import { getKeyName, numberWithComaToFixed } from 'ubs-framework/v2/common/function';
import { getCashfreeInstance } from '../../common/cashfree-loader';
import { BILLING_CYCLE_OPTIONS } from '../../../../../common/constant';

/**
 * Extract subscription ID from a Cashfree order_id.
 * Formats seen: "prorate_sub_XXX_TIMESTAMP", "sub_XXX"
 */
const extractSubscriptionId = (orderId) => {
    if (!orderId) return null;
    if (orderId.startsWith('prorate_')) {
        // "prorate_sub_ABC_123" → "sub_ABC"
        const parts = orderId.split('_');
        return parts.slice(1, parts.length - 1).join('_');
    }
    return orderId;
};

const MOD_TYPE_LABELS = {
    UPGRADE: 'Plan Upgraded Successfully',
    DOWNGRADE: 'Plan Modified',
    SEAT_INCREASE: 'Seats Added Successfully',
    SEAT_DECREASE: 'Seats Updated',
};

const upper = (value) => String(value || '').toUpperCase();

const CashfreeCallback = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const _ORG_ID = useSelector(e => e?.common?.orgId);
    const _PI = useSelector(e => e?.common?.planInfo);

    const [paymentStatus, setPaymentStatus] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [matchedCharge, setMatchedCharge] = useState(null);
    const [matchedMod, setMatchedMod] = useState(null);
    const [redirectMeta, setRedirectMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [countdown, setCountdown] = useState(10);
    const [completePaymentLoading, setCompletePaymentLoading] = useState(false);
    const countdownRef = useRef(null);
    const orderIdRef = useRef(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        orderIdRef.current = params.get('order_id') || params.get('orderId') || null;
        refresh();
        verifyPayment();
        return () => {
            off();
            clearInterval(countdownRef.current);
        };
    }, [location.search, _PI?.subscription]);

    const verifyPayment = () => {
        const params = new URLSearchParams(location.search);
        const orderId = params.get('order_id') || params.get('orderId');
        const directSubId = params.get('subscription_id');

        const subscriptionId =
            extractSubscriptionId(orderId) ||
            directSubId ||
            _PI?.subscription;

        if (orderId || subscriptionId) {
            setLoading(true);
            setRedirectMeta(null);
            sendData(dispatch, 'billing|cashfree|subscription|redirectStatus', {
                ...(orderId ? { orderId } : { subscriptionId }),
                syncWithCashfree: true,
            });
        } else {
            setLoading(false);
            clearInterval(countdownRef.current);
            setPaymentStatus('error');
        }
    };

    const mapPaymentStatus = (data, sub) => {
        const redirectType = upper(data?.redirectType);
        const checkoutStatus = upper(data?.checkoutStatus);
        const action = upper(data?.action);
        const statusLabel = upper(data?.statusLabel || sub?.statusLabel);

        if (redirectType === 'ORDER') {
            if (checkoutStatus === 'SUCCESS' && ['ALREADY_PAID_APPLIED', 'PAYMENT_CONFIRMED'].includes(action)) return 'success';
            if (checkoutStatus === 'FAILED' && ['PAYMENT_TERMINAL_RETRY_MODIFY', 'PAYMENT_FAILED'].includes(action)) return 'failed';
            if (checkoutStatus === 'PENDING') return 'pending';
        }

        if (redirectType === 'SUBSCRIPTION') {
            if (checkoutStatus === 'SUCCESS' && action === 'SUBSCRIPTION_STATUS') return 'success';
            if (checkoutStatus === 'FAILED' && action === 'SUBSCRIPTION_STATUS') return 'failed';
            if (checkoutStatus === 'PENDING' && ['SUBSCRIPTION_STATUS', 'WAIT_FOR_NEXT_CYCLE', 'RESUME_PAYMENT'].includes(action)) return 'pending';
        }

        if (data?.hasPendingPayment || action === 'RESUME_PAYMENT') return 'pending';
        if (checkoutStatus === 'SUCCESS' || statusLabel === 'ACTIVE') return 'success';
        if (checkoutStatus === 'FAILED' || ['FAILED', 'LINK_EXPIRED', 'EXPIRED', 'CARD_EXPIRED'].includes(statusLabel)) return 'failed';
        if (checkoutStatus === 'PENDING' || ['INITIATED', 'INITIALIZED', 'BANK_APPROVAL_PENDING'].includes(statusLabel)) return 'pending';
        return 'unknown';
    };

    const redirectToPricing = () => {
        history.push(`/${_ORG_ID}/manage/pricing-plan`);
    };

    const contactSupport = () => {
        redirectToSubscriptions();
    };

    const openPendingPayment = async () => {
        const paymentUrl = redirectMeta?.proratedOrder?.paymentUrl;
        const paymentLink = redirectMeta?.proratedOrder?.paymentLink;
        if (!paymentUrl && !paymentLink) return;

        try {
            setCompletePaymentLoading(true);

            if (paymentUrl) {
                window.open(paymentUrl, '_blank', 'noopener,noreferrer');
                return;
            }

            if (/^https?:\/\//i.test(paymentLink)) {
                window.open(paymentLink, '_blank', 'noopener,noreferrer');
                return;
            }

            const cashfreeInstance = await getCashfreeInstance();
            await cashfreeInstance.checkout({
                paymentSessionId: paymentLink,
                redirectTarget: '_self',
            });
        } catch (err) {
            // Swallow and keep callback page state unchanged; user can retry via CTA.
        } finally {
            setCompletePaymentLoading(false);
        }
    };

    const getActionButtons = () => {
        const make = (key, label, onClick, variant = 'blue') => ({ key, label, onClick, variant });

        let primary = null;
        let secondary = null;

        if (paymentStatus === 'success') {
            primary = make('default-success', 'Go to Payments', redirectToSubscriptions);
        } else if (paymentStatus === 'failed') {
            secondary = make('default-failed-secondary', 'Back to Payments', redirectToSubscriptions, 'white');
            primary = make('default-failed', 'Retry Payment', verifyPayment);
        } else if (paymentStatus === 'pending') {
            secondary = make('default-pending-secondary', 'Back to Payments', redirectToSubscriptions, 'white');
            primary = make('default-pending', 'Check Status', verifyPayment);
        } else {
            primary = make('default-unknown', 'Go to Subscriptions', redirectToSubscriptions);
        }

        const redirectType = upper(redirectMeta?.redirectType);
        const checkoutStatus = upper(redirectMeta?.checkoutStatus);
        const action = upper(redirectMeta?.action);

        if (redirectType === 'ORDER' && checkoutStatus === 'SUCCESS' && ['ALREADY_PAID_APPLIED', 'PAYMENT_CONFIRMED'].includes(action)) {
            primary = make('order-success-primary', 'Go to Payments', redirectToSubscriptions);
        } else if (redirectType === 'ORDER' && checkoutStatus === 'PENDING' && action === 'RESUME_PAYMENT') {
            primary = make('order-pending-resume-primary', 'Complete Payment', openPendingPayment);
            secondary = make('order-pending-resume-secondary', 'Refresh Status', verifyPayment, 'white');
        } else if (redirectType === 'ORDER' && checkoutStatus === 'FAILED' && ['PAYMENT_TERMINAL_RETRY_MODIFY', 'PAYMENT_FAILED'].includes(action)) {
            primary = make('order-failed-primary', 'Try Modify Again', redirectToPricing);
            secondary = make('order-failed-secondary', 'View Payments', redirectToSubscriptions, 'white');
        } else if (redirectType === 'ORDER' && checkoutStatus === 'PENDING') {
            primary = make('order-pending-primary', 'Refresh Status', verifyPayment);
            secondary = make('order-pending-secondary', 'View Payments', redirectToSubscriptions, 'white');
        } else if (redirectType === 'SUBSCRIPTION' && checkoutStatus === 'SUCCESS' && action === 'SUBSCRIPTION_STATUS') {
            primary = make('sub-success-secondary', 'View Payments', redirectToSubscriptions, 'white');
        } else if (redirectType === 'SUBSCRIPTION' && checkoutStatus === 'PENDING' && action === 'SUBSCRIPTION_STATUS') {
            primary = make('sub-pending-status-primary', 'Check Status', verifyPayment);
        } else if (redirectType === 'SUBSCRIPTION' && checkoutStatus === 'PENDING' && action === 'WAIT_FOR_NEXT_CYCLE') {
            primary = make('sub-pending-cycle-primary', 'Okay', redirectToSubscriptions);
            secondary = make('sub-pending-cycle-secondary', 'View Plan Details', redirectToPricing, 'white');
        } else if (redirectType === 'SUBSCRIPTION' && checkoutStatus === 'FAILED' && action === 'SUBSCRIPTION_STATUS') {
            primary = make('sub-failed-primary', 'Retry Subscription', redirectToPricing);
            secondary = make('sub-failed-secondary', 'Contact Support', contactSupport, 'white');
        } else if (redirectType === 'SUBSCRIPTION' && checkoutStatus === 'PENDING' && action === 'RESUME_PAYMENT') {
            primary = make('sub-pending-resume-primary', 'Complete Payment', openPendingPayment);
            secondary = make('sub-pending-resume-secondary', 'Refresh Status', verifyPayment, 'white');
        }

        const hasPendingPayment = !!redirectMeta?.hasPendingPayment;
        const hasPaymentLink = !!(redirectMeta?.proratedOrder?.paymentUrl || redirectMeta?.proratedOrder?.paymentLink);
        if (hasPendingPayment && hasPaymentLink && primary?.key !== 'complete-payment' && secondary?.key !== 'complete-payment') {
            secondary = make('refresh-status-secondary', 'Refresh Status', verifyPayment, 'white');
            primary = make('complete-payment', 'Complete Payment', openPendingPayment);
        }

        return { primary, secondary };
    };

    const { off, refresh } = useSocket(res => {
        if (!res.error) {
            switch (res.event) {
                case 'billing|cashfree|subscription|redirectStatus': {
                    setLoading(false);
                    const data = res?.data || {};
                    const sub = data?.subscription || null;
                    setRedirectMeta(data);
                    setSubscription(sub);
                    setMatchedCharge(data?.charge || null);
                    setMatchedMod(data?.pendingModification || null);

                    const mappedState = mapPaymentStatus(data, sub);
                    if (mappedState === 'success') {
                        setPaymentStatus('success');
                        startCountdown();
                    } else if (mappedState === 'failed') {
                        clearInterval(countdownRef.current);
                        setPaymentStatus('failed');
                    } else if (mappedState === 'pending') {
                        clearInterval(countdownRef.current);
                        setPaymentStatus('pending');
                    } else {
                        clearInterval(countdownRef.current);
                        setPaymentStatus('unknown');
                    }
                    break;
                }
                default:
                    return true;
            }
        } else {
            setLoading(false);
            clearInterval(countdownRef.current);
            setRedirectMeta(null);
            setPaymentStatus('error');
        }
    });

    const startCountdown = () => {
        clearInterval(countdownRef.current);
        let count = 10;
        setCountdown(10);
        countdownRef.current = setInterval(() => {
            count -= 1;
            setCountdown(count);
            if (count <= 0) {
                clearInterval(countdownRef.current);
                redirectToSubscriptions();
            }
        }, 1000);
    };

    const redirectToSubscriptions = () => {
        history.push(`/${_ORG_ID}/manage/subscriptions/payments`);
    };

    const getBillingCycleLabel = (cycle) => {
        return getKeyName(BILLING_CYCLE_OPTIONS, cycle)?.label || cycle || '-';
    };

    const formatAmount = (amount, currency) => {
        return numberWithComaToFixed(amount, currency || subscription?.currency);
    };

    const formatDate = (date) => date ? moment(date).format('DD MMM YYYY') : '-';

    const getSuccessTitle = () => {
        if (matchedMod) return MOD_TYPE_LABELS[matchedMod.type] || 'Payment Successful!';
        return 'Payment Successful!';
    };

    const renderIcon = (status) => {
        const map = {
            success: { IconComp: Icon.CheckMarkRound, cls: 'u_payment-success__icon--success', size: 44 },
            failed:  { IconComp: Icon.CloseMarkRound, cls: 'u_payment-success__icon--failed',  size: 40 },
            pending: { IconComp: Icon.Pending, cls: 'u_payment-success__icon--pending', size: 40 },
        };
        const cfg = map[status] || { IconComp: Icon.InfoTooltip, cls: 'u_payment-success__icon--unknown', size: 40 };
        return (
            <div className="u_payment-success__icon-wrap">
                <div className={`u_payment-success__icon ${cfg.cls}`}>
                    <cfg.IconComp size={cfg.size} fill="currentColor" />
                </div>
            </div>
        );
    };

    /**
     * Shows what changed: plan, seats, monthly amount (before → after).
     * Only rendered for proration / modification payments.
     */
    const renderChangeSummary = () => {
        if (!matchedMod?.before || !matchedMod?.after) return null;
        const { before, after } = matchedMod;

        const changes = [];
        if (before.planCode !== after.planCode) {
            changes.push({ label: 'Plan', before: before.planCode, after: after.planCode });
        }
        if (before.seatCount !== after.seatCount) {
            changes.push({ label: 'Seats', before: before.seatCount, after: after.seatCount });
        }
        if (before.totalAmount !== after.totalAmount) {
            changes.push({
                label: 'Monthly',
                before: formatAmount(before.totalAmount),
                after: formatAmount(after.totalAmount),
            });
        }
        if (!changes.length) return null;

        return (
            <div className="u_payment-success__change-block">
                {changes.map((c, i) => (
                    <div key={i} className="u_flex-align u_content-center u_gap-10">
                        <span className="u_small-font u_color-font u_mr-4">{c.label}:</span>
                        <span className="u_font u_fw-600 u_color-font">{c.before}</span>
                        <Icon.ArrowRightLine size={12} fill="currentColor" className="u_color-font u_mx-8" />
                        <span className="u_font u_fw-700 u_color-title">{c.after}</span>
                    </div>
                ))}
            </div>
        );
    };

    const renderPlanDetails = () => {
        if (!subscription) return null;

        const rows = [
            { label: 'Plan', value: subscription?.planCode || subscription?.planKey || matchedMod?.after?.planCode || '-' },
            { label: 'Billing Cycle', value: getBillingCycleLabel(subscription?.billingCycle) },
            { label: 'Seats', value: subscription?.seatCount ?? '-' },
            subscription?.perSeatPrice > 0 && { label: 'Per Seat', value: formatAmount(subscription.perSeatPrice) },
            subscription?.baseAmount > 0 && { label: 'Base Amount', value: formatAmount(subscription.baseAmount) },
            subscription?.gstAmount > 0 && { label: `GST (${subscription.gstRate > 1 ? subscription.gstRate : Math.round((subscription.gstRate || 0.18) * 100)}%)`, value: formatAmount(subscription.gstAmount) },
            subscription?.totalAmount > 0 && { label: 'Total (Monthly)', value: formatAmount(subscription.totalAmount), bold: true },
            { label: 'Next Billing', value: formatDate(subscription?.nextBillingDate || subscription?.nextPaymentDate) },
        ].filter(row => row !== false);

        return (
            <div className="u_payment-success__details">
                {rows?.map((row, i) => {
                    return (
                        <div key={row.label} className="u_payment-success__detail-row">
                            <span className="u_small-font u_color-font">{row.label}</span>
                            <span className={`u_font u_color-title ${row.bold ? 'u_fw-700' : 'u_fw-600'}`}>
                                {row.value}
                            </span>
                        </div>
                    )
                })}
            </div>
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="u_text-center">
                    <Icon.Preloader size={52} className="u_mb-20" />
                    <p className="u_semi-large-font u_fw-600 u_color-title u_mb-8">Verifying your payment...</p>
                    <p className="u_small-font u_color-font">Please wait while we confirm your transaction</p>
                </div>
            );
        }

        const { primary, secondary } = getActionButtons();
        const primaryType = (completePaymentLoading && primary?.key === 'complete-payment') ? 'Processing...' : primary?.label;

        if (paymentStatus === 'success') {
            // For proration: show the actual charge amount (e.g. ₹472), not subscription total (₹4,720)
            const amountPaid = matchedCharge?.amount;
            return (
                <>
                    {renderIcon('success')}

                    <h2 className="u_payment-success__title u_payment-success__title--success u_mb-6">
                        {getSuccessTitle()}
                    </h2>
                    <p className="u_small-font u_color-font u_mb-16">
                        Your subscription is now active and ready to use.
                    </p>

                    {amountPaid > 0 && (
                        <p className="u_payment-success__amount">
                            {formatAmount(amountPaid)}
                        </p>
                    )}

                    {renderChangeSummary()}
                    {renderPlanDetails()}

                    <div className="u_payment-success__countdown">
                        <div className="u_payment-success__countdown-bar" />
                    </div>
                    <p className="u_small-font u_color-font u_mb-6">
                        Redirecting in <strong>{countdown}s</strong>...
                    </p>

                    <div className="u_payment-success__actions">
                        {secondary && (
                            <UButton
                                variant={secondary?.variant || 'white'}
                                type={secondary?.label}
                                onClick={secondary?.onClick}
                                cn="u_payment-btn"
                            />
                        )}
                        {primary && (
                            <UButton
                                variant={primary?.variant || 'blue'}
                                type={primaryType}
                                onClick={primary?.onClick}
                                disabled={completePaymentLoading && primary?.key === 'complete-payment'}
                                cn="u_payment-btn"
                            />
                        )}
                    </div>
                </>
            );
        }

        if (paymentStatus === 'failed') {
            return (
                <>
                    {renderIcon('failed')}
                    <h2 className="u_payment-success__title u_payment-success__title--failed u_mb-8">
                        Payment Failed
                    </h2>
                    <p className="u_font u_color-font u_mb-4">
                        Unfortunately, your payment could not be processed. Please try again.
                    </p>
                    <div className="u_payment-success__actions">
                        {secondary && (
                            <UButton
                                variant={secondary?.variant || 'white'}
                                type={secondary?.label}
                                onClick={secondary?.onClick}
                                cn="u_payment-btn"
                            />
                        )}
                        {primary && (
                            <UButton
                                variant={primary?.variant || 'blue'}
                                type={primaryType}
                                onClick={primary?.onClick}
                                disabled={completePaymentLoading && primary?.key === 'complete-payment'}
                                cn="u_payment-btn"
                            />
                        )}
                    </div>
                </>
            );
        }

        if (paymentStatus === 'pending') {
            return (
                <>
                    {renderIcon('pending')}
                    <h2 className="u_payment-success__title u_payment-success__title--pending u_mb-8">
                        {['BANK_APPROVAL_PENDING']?.includes(redirectMeta?.statusLabel) ? 'Bank Approval Pending' : 'Payment Pending'}
                    </h2>
                    <p className="u_font u_color-font u_mb-4">
                        Your payment is being processed. This may take a few minutes.
                    </p>
                    <div className="u_payment-success__actions">
                        {secondary && (
                            <UButton
                                variant={secondary?.variant || 'white'}
                                type={secondary?.label}
                                onClick={secondary?.onClick}
                                cn="u_payment-btn"
                            />
                        )}
                        {primary && (
                            <UButton
                                variant={primary?.variant || 'blue'}
                                type={primaryType}
                                onClick={primary?.onClick}
                                disabled={completePaymentLoading && primary?.key === 'complete-payment'}
                                cn="u_payment-btn"
                            />
                        )}
                    </div>
                </>
            );
        }

        // unknown / error
        return (
            <>
                {renderIcon('unknown')}
                <h2 className="u_payment-success__title u_payment-success__title--unknown u_mb-8">
                    Something Went Wrong
                </h2>
                <p className="u_font u_color-font u_mb-4">
                    We couldn't verify your payment status. Please check your subscriptions or contact support.
                </p>
                <div className="u_payment-success__actions">
                    {secondary && (
                        <UButton
                            variant={secondary?.variant || 'white'}
                            type={secondary?.label}
                            onClick={secondary?.onClick}
                            cn="u_payment-btn"
                        />
                    )}
                    {primary && (
                        <UButton
                            variant={primary?.variant || 'blue'}
                            type={primaryType}
                            onClick={primary?.onClick}
                            disabled={completePaymentLoading && primary?.key === 'complete-payment'}
                            cn="u_payment-btn"
                        />
                    )}
                </div>
            </>
        );
    };

    return (
        <div className="u_pricing-plan">
            <div className="u_payment-success u_flex-align u_content-center">
                <div className="u_payment-success__card">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default CashfreeCallback;
